/**
 * CR-7388: My suppliers / All suppliers state that follows the URL, for the
 * PO Headers and Deliveries pages.
 *
 * The Classic Procurement Summary figures count the whole site and ignore
 * buyer codes, so its View links carry `?all_suppliers=true`. A recipient
 * holding buyer codes would otherwise land on My suppliers and see fewer
 * records than the figure. Same behaviour as PO Lines (CR-9095,
 * usePOLinesSupplierScope): the page opens on All suppliers when the URL says
 * so, and the switch writes its choice back, so switching to My suppliers
 * drops the parameter and a refresh keeps what the user picked.
 */
import { useCallback, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ALL_SUPPLIERS_PARAM } from './tabs/po-lines/usePOLinesSupplierScope';

/**
 * @returns `[mySuppliersOnly, setMySuppliersOnly]`, defaulting to My
 *   suppliers unless the URL carries `all_suppliers=true`.
 *
 * @example
 * const [mySuppliersOnly, setMySuppliersOnly] = useMySuppliersUrlState();
 */
export function useMySuppliersUrlState(): [boolean, (myOnly: boolean) => void] {
  const [searchParams, setSearchParams] = useSearchParams();
  const [mySuppliersOnly, setMyOnlyState] = useState(
    () => searchParams.get(ALL_SUPPLIERS_PARAM) !== 'true',
  );

  const setMySuppliersOnly = useCallback((myOnly: boolean) => {
    setMyOnlyState(myOnly);
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (myOnly) next.delete(ALL_SUPPLIERS_PARAM);
      else next.set(ALL_SUPPLIERS_PARAM, 'true');
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  return [mySuppliersOnly, setMySuppliersOnly];
}
